import React from "react";
import { Edit2 } from "lucide-react";
import Button from "../../common/ui/button/Button";
import {
  RequestParticipant,
  ParticipantStatus,
  ParticipantReminder,
  ReminderTiming,
} from "@secure-caseworker/shared";

type RemindersTabProps = {
  participants: RequestParticipant[];
  canEdit: boolean;
  hasDueDate: boolean;
  onEditReminders: (participant: RequestParticipant) => void;
  onEditAllReminders: () => void;
};

/**
 * Format a participant's reminder schedule into a human-readable string
 */
const formatReminderSchedule = (
  reminders: ParticipantReminder[] | undefined
): string => {
  if (!reminders || reminders.length === 0) {
    return "No reminders scheduled";
  }

  // Filter to only pending reminders
  const pendingReminders = reminders.filter((r) => r.status === "pending");

  if (pendingReminders.length === 0) {
    return "All reminders sent";
  }

  const parts: string[] = [];
  for (const reminder of pendingReminders) {
    if (reminder.timing === ReminderTiming.BEFORE_DUE) {
      parts.push(
        `${reminder.offset_days} day${
          reminder.offset_days !== 1 ? "s" : ""
        } before`
      );
    } else if (reminder.timing === ReminderTiming.ON_DUE) {
      parts.push("on due date");
    } else if (reminder.timing === ReminderTiming.AFTER_DUE) {
      parts.push(
        `${reminder.offset_days} day${
          reminder.offset_days !== 1 ? "s" : ""
        } after`
      );
    }
  }

  if (parts.length === 0) {
    return "No reminders scheduled";
  }

  return parts.join(", ");
};

/**
 * Get status message for a participant's reminders
 */
const getParticipantReminderStatus = (
  participant: RequestParticipant,
  hasDueDate: boolean
): string => {
  if (participant.status === ParticipantStatus.COMPLETED) {
    return "Completed - no more reminders";
  }

  if (participant.status === ParticipantStatus.DECLINED) {
    return "Declined - reminders canceled";
  }

  if (participant.status !== ParticipantStatus.PENDING) {
    return `Status: ${participant.status}`;
  }

  if (!hasDueDate) {
    return "No due date set - reminders disabled";
  }

  return formatReminderSchedule(participant.participant_reminders);
};

export const RemindersTab: React.FC<RemindersTabProps> = ({
  participants,
  canEdit,
  hasDueDate,
  onEditReminders,
  onEditAllReminders,
}) => {
  const pendingCount = participants.filter(
    (p) => p.status === ParticipantStatus.PENDING
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-black dark:text-white">
          Reminder Schedule
        </h3>
        {canEdit && pendingCount > 0 && (
          <Button variant="outline" size="sm" onClick={onEditAllReminders}>
            Edit All
          </Button>
        )}
      </div>

      {!hasDueDate && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Set a due date on the request to enable reminder scheduling.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {participants.map((participant) => (
          <div
            key={participant.id}
            className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            <div>
              <p className="font-medium text-black dark:text-white">
                {participant.name || participant.email}
              </p>
              <p className="text-sm text-gray-500">
                {getParticipantReminderStatus(participant, hasDueDate)}
              </p>
            </div>
            {canEdit &&
              participant.status === ParticipantStatus.PENDING &&
              hasDueDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditReminders(participant)}
                >
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
              )}
          </div>
        ))}
      </div>

      {participants.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No participants assigned to this request
        </div>
      )}
    </div>
  );
};
